import {ChangeDetectionStrategy, Component} from '@angular/core';
import {TableComponent} from '@aviary/components/table/table.component';
import {RouterActions, TableDefinition} from '@store/aviary/aviary.adapter';
import {CheckoutsSelectors} from '@store/checkouts/checkouts.selectors';
import {BorrowingHistoryRecord} from '@store/checkouts/checkouts.types';
import {CombinedCoverTitleDetailComponent} from '@lib/components/combined-cover-title-detail/combined-cover-title-detail.component';
import {ALWAYS} from '@aviary/components/table/table.component.types';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {ButtonRouteNameDirective} from '@lib/directives/button-route-name/button-route-name.directive';
import {HoldsSelectors} from '@store/holds/holds.selectors';
import {HoldsActions} from '@store/holds/holds.actions';
import {MxSelectorCreator} from '@aviary/store/store.types';
import {createSelector} from '@ngrx/store';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {CatalogActions} from '@store/catalog/catalog.actions';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {alwaysFalse} from '@aviary/store/common/common.selectors';

@Component({
  selector: 'px-borrowing-history',
  template: `
    @let loaded = selectors.finishedLoading | select:false;
    @let holdsLoaded = holdSelectors.finishedLoading | select:false;
    @let favoritesLoaded = favoriteSelectors.finishedLoading | select:false;
    <div class="flex items-center gap-x-3">
      <mx-icon-button icon="arrow_back" routeName="CHECKED_OUT" ariaLabel="Go Back"/>
      <span class="text-headline-medium">Borrowing History</span>
    </div>

    <mx-table mxHiddenUntilLoaded [reveal]="loaded && holdsLoaded && favoritesLoaded" [tableDefinition]="borrowingHistoryTableDefinition"/>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, IconButtonComponent, ButtonRouteNameDirective, SelectPipe, HiddenUntilLoadedDirective]
})
export class BorrowingHistoryComponent {
  public readonly selectors = CheckoutsSelectors.BorrowingHistory;
  public readonly holdSelectors = HoldsSelectors.HoldsForCatalogRecords;
  public readonly favoriteSelectors = CatalogSelectors.FavoritesIds;
  private readonly patronSelectors = PatronSelectors.AccountStatus;

  private readonly enableAddHoldIconFor: MxSelectorCreator<[BorrowingHistoryRecord], boolean> =
    (record) => createSelector(
      this.holdSelectors.showAddHoldIconFor(record.catalogRecordId),
      this.patronSelectors.hasBlocks,
      (showAddHold, hasBlocks) => showAddHold && !hasBlocks
    );

  private readonly addHoldTooltipFor: MxSelectorCreator<[BorrowingHistoryRecord], string> =
    (record) => createSelector(
      this.enableAddHoldIconFor(record),
      (enabled): string => enabled ? 'Place Hold' : 'Cannot place hold due to blocks on your account.'
    );

  // For placeholder hold button, to take up action space when we haven't figured out the state of the circ item's hold button yet on load.
  private readonly showHoldPlaceholderIconFor: MxSelectorCreator<[BorrowingHistoryRecord], boolean> =
    (record) => createSelector(
      this.holdSelectors.hideEditHoldIconFor(record.catalogRecordId),
      this.holdSelectors.hideViewHoldsIconFor(record.catalogRecordId),
      this.holdSelectors.hideAddHoldIconFor(record.catalogRecordId),
      (hideEdit, hideView, hideAdd) => hideEdit && hideView && hideAdd
    );

  public readonly borrowingHistoryTableDefinition: TableDefinition<BorrowingHistoryRecord, typeof CheckoutsSelectors.BorrowingHistory> = {
    defaultSelectorContainer: CheckoutsSelectors.BorrowingHistory,
    dataSource: 'borrowingHistory',
    responsiveColumnsDefinition: {
      verticalActionsSm: true,
      responsiveHideHeaderRow: true,
      columnNamesSm: ['itemSm'],
      columnNamesMd: ['checkinDate', 'itemMd', 'checkoutDate'],
      columnNamesLg: ['checkinDate', 'itemLg', 'author', 'materialTypeDescription', 'checkoutDate']
    },
    columns: [
      {
        type: 'text',
        value: 'checkinDateFor',
        header: 'Returned',
        format: 'DATE_ONLY',
        headerCssClasses: 'w-[15%]',
        sortable: true,
        sortKey: 'returnedDate'
      },
      {
        type: 'component',
        columnName: 'itemLg',
        header: 'Item',
        componentClass: CombinedCoverTitleDetailComponent,
        componentProperties: {
          catalogRecord: 'buildCatalogRecordFor'
        },
        sortable: true,
        sortKey: 'title'
      },
      {
        type: 'component',
        columnName: 'itemMd',
        header: 'Item',
        componentClass: CombinedCoverTitleDetailComponent,
        componentProperties: {
          catalogRecord: 'buildCatalogRecordFor',
          detail1: 'authorFor',
          detail2: 'materialTypeDescriptionFor',
        },
        sortable: true,
        sortKey: 'title'
      },
      {
        type: 'component',
        columnName: 'itemSm',
        headerCssClasses: 'grow',
        componentClass: CombinedCoverTitleDetailComponent,
        componentProperties: {
          catalogRecord: 'buildCatalogRecordFor',
          showCover: false,
          detail1: 'authorFor',
          detail2: 'materialTypeDescriptionFor',
          date1Label: 'Returned',
          date1: 'checkinDateFor',
          date2Label: 'Checked Out',
          date2: 'checkoutDateFor',
        },
        sortable: true,
        sortKey: 'title'
      },
      {
        type: 'text',
        value: 'authorFor',
        header: 'Author',
        headerCssClasses: 'w-[15%]',
        sortable: true,
        sortKey: 'author'
      },
      {
        type: 'text',
        value: 'materialTypeDescriptionFor',
        header: 'Material Type',
        format: 'TITLE_CASE',
        headerCssClasses: 'w-[15%]',
        sortable: true,
        sortKey: 'materialType'
      },
      {
        type: 'text',
        value: 'checkoutDateFor',
        header: 'Checked Out',
        format: 'DATE_ONLY',
        headerCssClasses: 'w-[15%]',
        sortable: true,
        sortKey: 'checkedOutDate'
      },
    ],
    actionIconButtons: [
      // Only ONE of the following favorite-related action icons can be visible on the row at any given time.
      {
        iconName: 'favorite_outlined',
        tooltip: 'Add to favorites',
        cssClasses: {'material-icons-outlined': ALWAYS},
        visible: (record) => this.favoriteSelectors.isNotFavorite(record.catalogRecordId),
        clickActionCreator: (record) => CatalogActions.addFavorite(record.catalogRecordId),
      },
      {
        iconName: 'favorite',
        tooltip: 'Favorite',
        cssClasses: {'text-error': ALWAYS},
        visible: (record) => this.favoriteSelectors.isFavorite(record.catalogRecordId),
        clickActionCreator: (record) => CatalogActions.removeFavoriteByCatalogRecordId(record.catalogRecordId),
      },

      // Only ONE of the following hold-related action icons can be visible on the row at any given time.
      {
        iconName: 'bookmark',
        tooltip: (record) => this.holdSelectors.editHoldTooltip(record.catalogRecordId),
        cssClasses: { 'text-primary': ALWAYS },
        visible: (record) => this.holdSelectors.showEditHoldIconFor(record.catalogRecordId),
        enabled: (record) => this.holdSelectors.singleHoldIsEditable(record.catalogRecordId),
        clickActionCreator: (record) => HoldsActions.editHoldByCatalogRecordId(record.catalogRecordId)
      },
      {
        iconName: 'bookmark',
        tooltip: 'View Holds',
        cssClasses: { 'text-primary': ALWAYS },
        visible: (record) => this.holdSelectors.showViewHoldsIconFor(record.catalogRecordId),
        clickActionCreator: () => RouterActions.navigateTo('HOLDS')
      },
      {
        iconName: 'bookmark_add',
        tooltip: this.addHoldTooltipFor,
        cssClasses: {
          'material-icons-outlined': ALWAYS,
          'text-on-surface-variant': ALWAYS
        },
        visible: (record) => this.holdSelectors.showAddHoldIconFor(record.catalogRecordId),
        enabled: this.enableAddHoldIconFor,
        clickActionCreator: (record) => HoldsActions.placeHoldByCatalogRecordId(record.catalogRecordId)
      },

      // This action icon is pure trickery - it doesn't do anything and will be hidden once the rest of the hold icon selectors are settled.  This stops the
      // Actions column from collapsing when no second action is visible in the row on the initial table load.
      {
        iconName: 'bookmark_border',
        visible: this.showHoldPlaceholderIconFor,
        cssClasses: { 'opacity-[0.001]': ALWAYS },
        enabled: () => alwaysFalse
      }
    ]
  };
}
